import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { X, Pencil, AlertTriangle, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Code, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { generateWidgetDataBridge } from "@/lib/widgetDataBridge";

interface SandboxedViewProps {
  /** The full HTML document string to render inside the sandbox */
  htmlContent: string;
  /** Maximum height in pixels (defaults to 260) */
  maxHeight?: number;
  /** Whether to show the edit/delete overlay on click */
  interactive?: boolean;
  /** Called when the user clicks the delete (X) button */
  onDelete?: () => void;
  /** Called when the user clicks the edit (pencil) button */
  onEdit?: () => void;
  /** Optional extra className */
  className?: string;
  /** Whether the widget is in half-width (minimized) mode */
  isHalf?: boolean;
  /** AI explanation text for this widget */
  explanation?: string | null;
  /** Movement callbacks — only provided when movement is possible */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
}

/**
 * Renders AI-generated HTML/CSS/JS inside a sandboxed iframe.
 * Uses fixed aspect ratios:
 *   - Full-width: 16 / 9
 *   - Half-width: 4 / 3
 */
export const SandboxedView = ({
  htmlContent,
  maxHeight: _maxHeight,          // kept for API compat — ignored
  interactive = false,
  onDelete,
  onEdit,
  className = "",
  isHalf = false,
  explanation,
  onMoveUp,
  onMoveDown,
  onMoveLeft,
  onMoveRight,
}: SandboxedViewProps) => {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [showControls, setShowControls] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [authToken, setAuthToken] = useState<string>("");
  const [authUserId, setAuthUserId] = useState<string>("");

  // Keep auth token fresh so iframe always has a valid JWT
  useEffect(() => {
    // Seed with current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthToken(session?.access_token || "");
      setAuthUserId(session?.user?.id || "");
    }).catch(() => {});

    // Listen for token refreshes & sign-in/out
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setAuthToken(session?.access_token || "");
        setAuthUserId(session?.user?.id || "");
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  // Inject the HTML into a blob URL so the iframe is truly sandboxed
  const blobUrl = useRef<string | null>(null);

  useEffect(() => {
    // Don't build the iframe until we have a valid token
    if (!authToken || !authUserId) return;

    let cancelled = false;

    async function buildIframe() {
      // Clean up previous blob URL
      if (blobUrl.current) {
        URL.revokeObjectURL(blobUrl.current);
      }

      const token = authToken;
      const userId = authUserId;

      if (cancelled) return;

      const sbUrl = import.meta.env.VITE_SUPABASE_URL || "";
      const sbKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "";

      try {
        // Wrap the AI-generated content with error handling & height reporting
        const wrappedHtml = `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline' https://cdn.jsdelivr.net; style-src 'unsafe-inline' https://fonts.googleapis.com; font-src https://fonts.gstatic.com; connect-src ${sbUrl}; img-src data: blob:;"/>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    font-family: 'Poppins', sans-serif;
    background: transparent;
    color: #e4e4e7;
    -webkit-font-smoothing: antialiased;
    width: 100vw; height: 100vh;
    margin: 0; padding: 0;
    overflow: hidden;
  }
  #root { width: 100vw; height: 100vh; overflow-x: hidden; overflow-y: auto; }
  #root::-webkit-scrollbar { width: 4px; }
  #root::-webkit-scrollbar-track { background: transparent; }
  #root::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 4px; }
  #root::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
  img, svg, canvas, video { max-width: 100%; }
  table { max-width: 100%; overflow-x: auto; display: block; }
</style>
<script>
// Supabase credentials injected BEFORE content so AI scripts can use them immediately
window.__SUPABASE_URL__ = ${JSON.stringify(sbUrl)};
window.__SUPABASE_KEY__ = ${JSON.stringify(sbKey)};
window.__USER_TOKEN__ = ${JSON.stringify(token)};
window.__USER_ID__ = ${JSON.stringify(userId)};
</script>
<script>
// GitfitData runtime bridge — gives widgets the same data access the AI backend has
${generateWidgetDataBridge()}
</script>
</head>
<body>
<div id="root">
${htmlContent}
</div>
<script>
// Catch all uncaught errors and forward to parent
window.onerror = function(msg) {
  window.parent.postMessage({ type: 'sandbox-error', message: String(msg) }, '*');
  return true;
};
window.addEventListener('unhandledrejection', function(e) {
  window.parent.postMessage({ type: 'sandbox-error', message: String(e.reason) }, '*');
});
</script>

</body>
</html>`;

        const blob = new Blob([wrappedHtml], { type: "text/html" });
        blobUrl.current = URL.createObjectURL(blob);
        setHasError(false);

        if (iframeRef.current) {
          iframeRef.current.src = blobUrl.current;
        }
      } catch {
        setHasError(true);
      }
    }

    buildIframe();

    return () => {
      cancelled = true;
      if (blobUrl.current) {
        URL.revokeObjectURL(blobUrl.current);
        blobUrl.current = null;
      }
    };
  }, [htmlContent, authToken, authUserId]);

  // Listen for error messages from the sandboxed iframe
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.origin !== "null") return;
      if (e.data?.type === "sandbox-error") {
        console.warn("[SandboxedView] iframe error:", e.data.message);
        setHasError(true);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const handleClick = useCallback(() => {
    if (interactive) {
      setShowControls((prev) => !prev);
    }
  }, [interactive]);

  // Hide controls when clicking outside
  useEffect(() => {
    if (!showControls) return;
    const hide = () => setShowControls(false);
    const timer = setTimeout(() => {
      document.addEventListener("click", hide, { once: true });
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", hide);
    };
  }, [showControls]);

  if (hasError) {
    return (
      <div
        className={`relative rounded-xl bg-muted/50 border border-border flex items-center justify-center ${className}`}
        style={{ height: 120 }}
      >
        <div className="flex flex-col items-center gap-1 text-muted-foreground text-xs">
          <AlertTriangle className="h-5 w-5" />
          <span>{t('custom_views.failed_to_load', 'Widget failed to load')}</span>
        </div>
        {interactive && onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="absolute top-2 right-2 p-1 rounded-full bg-red-500/90 text-white hover:bg-red-600 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-xl overflow-hidden bg-card border border-border ${className}`}
      style={{ aspectRatio: isHalf ? '4 / 3' : '16 / 9', maxHeight: isHalf ? '45vh' : '50vh' }}
    >
      <iframe
        ref={iframeRef}
        sandbox="allow-scripts"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          border: "none",
          display: "block",
        }}
        title={t('custom_views.title', 'Custom Widget')}
      />

      {/* Transparent click-catcher for interactive mode (allows iframe scrolling underneath) */}
      {interactive && !showControls && (
        <div
          className="absolute inset-0 z-[5]"
          onClick={handleClick}
        />
      )}

      {/* Controls overlay */}
      {interactive && showControls && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="absolute inset-0 bg-black/20 backdrop-blur-[1px] rounded-xl"
          onClick={(e) => { e.stopPropagation(); setShowControls(false); }}
        >
          {/* Delete button - top right */}
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/90 text-white hover:bg-red-600 transition-colors shadow-md"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          {/* Directional controls - center */}
          {(onMoveUp || onMoveDown || onMoveLeft || onMoveRight) && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="flex flex-col items-center gap-0.5 pointer-events-auto">
                {onMoveUp && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                    className="p-1 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors shadow-md"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                )}
                <div className="flex items-center gap-0.5">
                  {onMoveLeft && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onMoveLeft(); }}
                      className="p-1 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors shadow-md"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {onMoveRight && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onMoveRight(); }}
                      className="p-1 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors shadow-md"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {onMoveDown && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                    className="p-1 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors shadow-md"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Show code button - bottom left */}
          <div className="absolute bottom-2 left-2 flex gap-1.5">
            <button
              onClick={(e) => { e.stopPropagation(); setShowCode(true); setShowControls(false); }}
              className="p-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors shadow-md"
              title={t('custom_views.show_code', 'View Source')}
            >
              <Code className="h-4 w-4" />
            </button>
            {explanation && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowExplanation(true); setShowControls(false); }}
                className="p-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors shadow-md"
                title={t('custom_views.show_explanation', 'View Explanation')}
              >
                <FileText className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Edit button - bottom right */}
          {onEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="absolute bottom-2 right-2 p-1.5 rounded-full bg-blue-500/90 text-white hover:bg-blue-600 transition-colors shadow-md"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </motion.div>
      )}

      {/* Source code overlay */}
      {showCode && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="absolute inset-0 z-10 rounded-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Blurred widget background */}
          <div className="absolute inset-0 backdrop-blur-md bg-black/70" />

          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowCode(false); }}
            className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors shadow-md"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Code label */}
          <div className="absolute top-2 left-3 z-20 flex items-center gap-1.5 text-[10px] font-medium text-zinc-400 uppercase tracking-wider">
            <Code className="h-3 w-3" />
            <span>Source</span>
          </div>

          {/* Scrollable code */}
          <pre
            className="absolute inset-0 z-10 overflow-auto p-3 pt-8 text-[10px] leading-relaxed text-green-300/90 font-mono whitespace-pre-wrap break-all select-text"
            style={{ WebkitOverflowScrolling: 'touch', pointerEvents: 'auto' }}
          >{htmlContent}</pre>
        </motion.div>
      )}

      {/* Explanation overlay */}
      {showExplanation && explanation && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="absolute inset-0 z-10 rounded-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Blurred widget background */}
          <div className="absolute inset-0 backdrop-blur-md bg-black/70" />

          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowExplanation(false); }}
            className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors shadow-md"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Explanation label */}
          <div className="absolute top-2 left-3 z-20 flex items-center gap-1.5 text-[10px] font-medium text-zinc-400 uppercase tracking-wider">
            <FileText className="h-3 w-3" />
            <span>Explanation</span>
          </div>

          {/* Scrollable explanation text */}
          <div
            className="absolute inset-0 z-10 overflow-auto p-3 pt-8 text-[11px] leading-relaxed text-zinc-200 whitespace-pre-wrap break-words select-text"
            style={{ WebkitOverflowScrolling: 'touch', pointerEvents: 'auto', fontFamily: "'Poppins', sans-serif" }}
          >{explanation}</div>
        </motion.div>
      )}
    </div>
  );
};
